import * as Sentry from '@sentry/vue';
import type { OnCreateAppSync } from 'vike-vue/types';
import router from '../router-ssr';
import 'iconify-icon';
import { createGtm, type GtmQueryParams } from '@gtm-support/vue-gtm';
import gettext from '../gettext';
import {
  type EventIdPageContext,
  seedEventIdFromPageContext,
} from '../components/tracking/eventDataLayer';

import Toast, { POSITION } from 'vue-toastification';
import { createPinia } from 'pinia';

export const onCreateApp: OnCreateAppSync = (pageContext): ReturnType<OnCreateAppSync> => {
  if (pageContext.isRenderingHead) {
    return;
  }

  installPinia(pageContext);

  const { app } = pageContext;

  // MUST stay above the createGtm() call below. That call synchronously creates the
  // gtm.js script element, and GTM replays the dataLayer in order on init — so seeding
  // here is what puts the event id ahead of the container's own first page view. Move
  // it below and that page view silently loses the dimension, with no test to catch it.
  seedEventIdFromPageContext(pageContext as EventIdPageContext);

  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
    });
  }

  if (import.meta.env.VITE_GTM_ID) {
    let gtmParams: GtmQueryParams | undefined;
    try {
      gtmParams = JSON.parse(import.meta.env.VITE_GTM_PARAMS);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // We don't care if it failed, maybe it wasn't set
    }

    app.use(
      createGtm({
        id: import.meta.env.VITE_GTM_ID,
        queryParams: gtmParams,
      }),
    );
  }

  app.use(gettext).use(router).use(Toast, {
    position: POSITION.TOP_RIGHT,
    timeout: 3000,
    hideProgressBar: true,
    closeButton: false,
    transition: 'Vue-Toastification__fade',
  });
};

type PageContext = Parameters<typeof onCreateApp>[0];
const installPinia = (pageContext: PageContext) => {
  const pinia = createPinia();
  pageContext.app.use(pinia);
  Object.assign(pageContext, { pinia });
};
